<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const stagesList = computed(() => {
  if (!props.data?.by_stage) return [];
  return Object.entries(props.data.by_stage)
    .map(([key, stage]) => ({
      key,
      ...stage,
    }))
    .sort((a, b) => a.position - b.position);
});

const maxDays = computed(() => {
  if (!stagesList.value.length) return 1;
  return Math.max(...stagesList.value.map(s => s.avg_days_in_stage || 0), 1);
});

const getBarWidth = days => {
  const percentage = (days / maxDays.value) * 100;
  return `${Math.max(percentage, 5)}%`;
};

const getBarColor = stage => {
  if (stage.is_bottleneck) return 'bg-red-500';
  const ratio = stage.avg_days_in_stage / (stage.max_days_configured || 14);
  if (ratio >= 0.8) return 'bg-orange-500';
  if (ratio >= 0.5) return 'bg-yellow-500';
  return 'bg-green-500';
};

const bottlenecks = computed(() => props.data?.bottlenecks || []);
</script>

<template>
  <div
    class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6"
  >
    <div class="flex items-center justify-between mb-6">
      <div>
        <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
          {{ t('DEAL_REPORTS.VELOCITY.TITLE') }}
        </h3>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ t('DEAL_REPORTS.VELOCITY.SUBTITLE') }}
        </p>
      </div>
      <div v-if="data" class="text-right">
        <p class="text-2xl font-bold text-slate-900 dark:text-white">
          {{ data.overall_velocity_days }}
        </p>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          {{ t('DEAL_REPORTS.VELOCITY.AVG_DAYS') }}
        </p>
      </div>
    </div>

    <!-- Content -->
    <div v-if="data" class="space-y-6">
      <!-- Bottlenecks Alert -->
      <div
        v-if="bottlenecks.length > 0"
        class="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
      >
        <div class="flex items-center gap-2">
          <span class="i-lucide-alert-triangle text-red-500" />
          <span class="text-sm font-medium text-red-700 dark:text-red-400">
            {{ t('DEAL_REPORTS.VELOCITY.BOTTLENECKS_FOUND') }}
          </span>
        </div>
        <p class="text-xs text-red-600 dark:text-red-400 mt-1">
          {{ bottlenecks.join(', ') }}
        </p>
      </div>

      <!-- Stage Velocity Bars -->
      <div class="space-y-4">
        <div v-for="stage in stagesList" :key="stage.key" class="space-y-2">
          <div class="flex items-center justify-between text-sm">
            <div class="flex items-center gap-2">
              <span class="text-slate-700 dark:text-slate-300">
                {{ stage.label }}
              </span>
              <span
                v-if="stage.is_bottleneck"
                class="px-1.5 py-0.5 text-[10px] rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
              >
                {{ t('DEAL_REPORTS.VELOCITY.BOTTLENECK') }}
              </span>
            </div>
            <div
              class="flex items-center gap-2 text-slate-500 dark:text-slate-400"
            >
              <span class="font-medium text-slate-900 dark:text-white">
                {{ stage.avg_days_in_stage }}d
              </span>
              <span class="text-xs">
                / {{ stage.max_days_configured }}d max
              </span>
              <span class="text-xs">
                ({{ stage.current_deals }}
                {{ t('DEAL_REPORTS.VELOCITY.OPEN') }})
              </span>
            </div>
          </div>
          <div
            class="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden"
          >
            <div
              class="h-full rounded-full transition-all duration-300"
              :class="getBarColor(stage)"
              :style="{ width: getBarWidth(stage.avg_days_in_stage) }"
            />
          </div>
        </div>
      </div>

      <!-- Legend -->
      <div
        class="flex items-center justify-center gap-4 pt-4 border-t border-slate-200 dark:border-slate-700"
      >
        <div class="flex items-center gap-1.5 text-xs">
          <span class="w-2 h-2 rounded-full bg-green-500" />
          <span class="text-slate-500 dark:text-slate-400">{{
            t('DEAL_REPORTS.VELOCITY.GOOD')
          }}</span>
        </div>
        <div class="flex items-center gap-1.5 text-xs">
          <span class="w-2 h-2 rounded-full bg-yellow-500" />
          <span class="text-slate-500 dark:text-slate-400">{{
            t('DEAL_REPORTS.VELOCITY.SLOW')
          }}</span>
        </div>
        <div class="flex items-center gap-1.5 text-xs">
          <span class="w-2 h-2 rounded-full bg-orange-500" />
          <span class="text-slate-500 dark:text-slate-400">{{
            t('DEAL_REPORTS.VELOCITY.AT_RISK')
          }}</span>
        </div>
        <div class="flex items-center gap-1.5 text-xs">
          <span class="w-2 h-2 rounded-full bg-red-500" />
          <span class="text-slate-500 dark:text-slate-400">{{
            t('DEAL_REPORTS.VELOCITY.BOTTLENECK')
          }}</span>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex items-center justify-center h-48">
      <p class="text-slate-500 dark:text-slate-400">
        {{ t('DEAL_REPORTS.NO_DATA') }}
      </p>
    </div>
  </div>
</template>
